import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ClassFeeStructure,
  FeePaymentRecord,
  RecordFeePaymentDto,
  SalaryRecord,
  StudentFeeAccount
} from '../models/finance.model';
import { Student } from '../student';

const LS_STRUCTURES = 'eschool-finance-fee-structures-v1';
const LS_LEDGERS = 'eschool-finance-ledgers-v1';
const LS_SALARIES = 'eschool-finance-salaries-v1';

/**
 * Finance & accounts APIs. Falls back to localStorage when endpoints are absent.
 *
 * Suggested Spring paths (align to your controllers):
 * - GET/PUT `/api/v1/finance/fee-structures` → `ClassFeeStructure[]`
 * - GET/PUT `/api/v1/finance/student-ledgers` → `StudentFeeAccount[]` (full list; Accounts). **Expected fees and balance** are persisted on each row by the backend.
 * - GET `/api/v1/finance/student-ledgers/{studentId}` → one `StudentFeeAccount` (student profile)
 * - POST `/api/v1/finance/payments` → body `RecordFeePaymentDto`, returns updated ledger or void
 * - GET/PUT `/api/v1/finance/salaries` → `SalaryRecord[]`
 */
@Injectable({
  providedIn: 'root'
})
export class FinanceService {

  private readonly base = `${environment.apiUrl}/api/v1/finance`;

  constructor(private http: HttpClient) { }

  getFeeStructures(): Observable<ClassFeeStructure[]> {
    return this.http.get<ClassFeeStructure[]>(`${this.base}/fee-structures`).pipe(
      tap(s => this.cacheStructures(s)),
      catchError(() => of(this.readStructures()))
    );
  }

  saveFeeStructures(structures: ClassFeeStructure[]): Observable<void> {
    window.localStorage.setItem(LS_STRUCTURES, JSON.stringify(structures));
    return this.http.put<void>(`${this.base}/fee-structures`, structures).pipe(
      catchError(() => of(undefined))
    );
  }

  getLedgers(): Observable<StudentFeeAccount[]> {
    return this.http.get<unknown[]>(`${this.base}/student-ledgers`).pipe(
      map(rows => (Array.isArray(rows) ? rows : []).map(r => this.normalizeLedgerRow(r))),
      tap(l => window.localStorage.setItem(LS_LEDGERS, JSON.stringify(l))),
      catchError(() => of(this.readLedgers()))
    );
  }

  /**
   * Single student fee ledger (preferred on student detail).
   * Backend: GET `/api/v1/finance/student-ledgers/{studentId}` → `StudentFeeAccount`.
   * If the request fails (e.g. endpoint not deployed), falls back to cached full list in localStorage.
   */
  getLedgerByStudentId(studentId: number): Observable<StudentFeeAccount | null> {
    return this.http.get<unknown>(`${this.base}/student-ledgers/${studentId}`).pipe(
      map(body => this.normalizeLedgerRow(body)),
      catchError(() => {
        const row = this.readLedgers().find(l => l.studentId === studentId);
        return of(row ?? null);
      })
    );
  }

  saveLedgers(ledgers: StudentFeeAccount[]): Observable<void> {
    window.localStorage.setItem(LS_LEDGERS, JSON.stringify(ledgers));
    return this.http.put<void>(`${this.base}/student-ledgers`, ledgers).pipe(
      catchError(() => of(undefined))
    );
  }

  postPayment(dto: RecordFeePaymentDto): Observable<StudentFeeAccount | void> {
    return this.http.post<unknown>(`${this.base}/payments`, dto).pipe(
      map(body => {
        if (body == null || typeof body !== 'object' || !Object.keys(body as object).length) {
          return undefined;
        }
        return this.normalizeLedgerRow(body);
      }),
      catchError(() => of(undefined))
    );
  }

  getSalaries(): Observable<SalaryRecord[]> {
    return this.http.get<SalaryRecord[]>(`${this.base}/salaries`).pipe(
      tap(s => window.localStorage.setItem(LS_SALARIES, JSON.stringify(s))),
      catchError(() => of(this.readSalaries()))
    );
  }

  saveSalaries(rows: SalaryRecord[]): Observable<void> {
    window.localStorage.setItem(LS_SALARIES, JSON.stringify(rows));
    return this.http.put<void>(`${this.base}/salaries`, rows).pipe(
      catchError(() => of(undefined))
    );
  }

  /** Sum of fee line amounts for a class in the given academic year (fee-structure editor / admin). */
  annualTotalForClass(structures: ClassFeeStructure[], className: string, academicYear: string): number {
    const s = structures.find(
      x => x.className === className && x.academicYear === academicYear
    );
    if (!s || !s.lines?.length) {
      return 0;
    }
    return s.lines.reduce((a, l) => a + (Number(l.amount) || 0), 0);
  }

  /**
   * Overlays latest student name and class onto ledger rows from the API.
   * **Expected fees, paid totals, and balance** come from the backend ledger only.
   */
  mergeLedgersWithStudents(students: Student[], ledgers: StudentFeeAccount[]): StudentFeeAccount[] {
    const list = Array.isArray(ledgers) ? ledgers : [];
    const byId = new Map(students.filter(s => s.id != null).map(s => [s.id as number, s]));
    const enriched = list.map(l => {
      const st = byId.get(l.studentId);
      const fromStudent = st ? `${st.firstName || ''} ${st.lastName || ''}`.trim() : '';
      const fromLedger = l.name != null ? String(l.name).trim() : '';
      const name = (fromStudent || fromLedger || `Student #${l.studentId}`).trim();
      const className =
        st?.className != null && String(st.className).trim()
          ? String(st.className).trim()
          : (l.className != null ? String(l.className) : '');
      return { ...l, name, className };
    });
    return enriched.sort((a, b) =>
      String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
    );
  }

  private cacheStructures(s: ClassFeeStructure[]): void {
    if (s && s.length) {
      window.localStorage.setItem(LS_STRUCTURES, JSON.stringify(s));
    }
  }

  private readStructures(): ClassFeeStructure[] {
    const raw = window.localStorage.getItem(LS_STRUCTURES);
    if (!raw) {
      return [];
    }
    try {
      return JSON.parse(raw) as ClassFeeStructure[];
    } catch {
      return [];
    }
  }

  private readLedgers(): StudentFeeAccount[] {
    const raw = window.localStorage.getItem(LS_LEDGERS);
    if (!raw) {
      return [];
    }
    try {
      const parsed = JSON.parse(raw) as unknown[];
      return Array.isArray(parsed) ? parsed.map(r => this.normalizeLedgerRow(r)) : [];
    } catch {
      return [];
    }
  }

  /**
   * Maps backend ledger JSON (e.g. `annualTotalFee`, `studentDisplayName`) onto {@link StudentFeeAccount}.
   */
  private normalizeLedgerRow(raw: unknown): StudentFeeAccount {
    if (raw == null || typeof raw !== 'object') {
      return {
        studentId: 0,
        name: '',
        className: '',
        annualTotal: 0,
        totalPaid: 0,
        payments: []
      };
    }
    const o = raw as Record<string, unknown>;
    const n = (v: unknown): number => {
      const x = Number(v);
      return Number.isFinite(x) ? x : 0;
    };
    const studentId = n(o['studentId']);
    const nameRaw = o['name'] ?? o['studentDisplayName'];
    const name = nameRaw != null ? String(nameRaw).trim() : '';
    const className = o['className'] != null ? String(o['className']).trim() : '';
    const annualFromTotal = n(o['annualTotal']);
    const annualFromFee = n(o['annualTotalFee']);
    const annualTotal = annualFromTotal > 0 ? annualFromTotal : annualFromFee;
    const totalPaid = n(o['totalPaid']);
    const payments = Array.isArray(o['payments']) ? (o['payments'] as FeePaymentRecord[]) : [];
    let balanceDue: number | undefined;
    if (o['balanceDue'] != null && o['balanceDue'] !== '') {
      const b = Number(o['balanceDue']);
      if (Number.isFinite(b)) {
        balanceDue = b;
      }
    }
    const idRaw = o['id'];
    const id =
      idRaw != null && idRaw !== '' && Number.isFinite(Number(idRaw)) ? Number(idRaw) : undefined;
    const studentRollNumber =
      o['studentRollNumber'] != null ? String(o['studentRollNumber']) : undefined;
    const currency = o['currency'] != null ? String(o['currency']) : undefined;
    const academicYear =
      o['academicYear'] === null || o['academicYear'] === undefined
        ? undefined
        : String(o['academicYear']);
    return {
      id,
      studentId,
      name,
      className,
      annualTotal,
      totalPaid,
      payments,
      balanceDue,
      currency,
      academicYear,
      annualTotalFee: annualFromFee > 0 ? annualFromFee : undefined,
      studentDisplayName: o['studentDisplayName'] != null ? String(o['studentDisplayName']) : undefined,
      studentRollNumber
    };
  }

  private readSalaries(): SalaryRecord[] {
    const raw = window.localStorage.getItem(LS_SALARIES);
    if (!raw) {
      return [];
    }
    try {
      return JSON.parse(raw) as SalaryRecord[];
    } catch {
      return [];
    }
  }
}
